import React, { useCallback, useEffect, useState } from 'react';

import { Box, DrawerOverlay, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { closeObjectiveDetailsPanelActionCreator } from 'actions/objectiveDetailsPanelActions';
import { BreadCrumbComponent } from 'components/RabbitInitiativeSidePanel/BreadCrumbComponent';
import MeshIconButton from 'components/RabbitOKRCreationPanel/components/MeshIconButton';
import { SectionHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import {
  openCreateOKRPanel,
  SECONDARY_PANEL_TYPES,
  openChangeDetailsPanel,
  openTitleAndMetricPanel,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { themeColors } from 'themes/clients/baseTheme';
import { checkGoalGenAIEnabled } from 'utils/helpers/featureFlags';

import {
  ArrowBack,
  KeyResultIcon,
  KeyResultPrivate2,
  ObjectiveIcon,
  PrivateObjective,
} from 'assets/assetsV1/iconComponents';

import {
  OKR_TYPES,
  OKR_PROGRESS_TYPES,
  CLOSED_STATUS_COLOR_MAP,
  INPROGRESS_STATUS_COLOR_MAP,
} from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import {
  getRouteAfterRemovingTaskDetails,
  getRouteAfterRemovingObjectiveDetailsAndKRDetails,
  getParsedPanelStack,
  getSearchStringFromLatestPanelStack,
} from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendAIButtonClickedEvent, sendMeshMavenClickedEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { GOALS_EVENT_SOURCES } from 'constants/trackingEvents';

import {
  BottomDrawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  primaryDrawerWidth,
  secondaryDrawerWidth,
} from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import DataUnavailable from './Components/DataUnavailable';
import { OKRHeaderInput } from './Components/OKRHeaderInput';
import { OKRKebabMenu } from './Components/OKRKebabMenu';
import { resetKR, updateKR } from './KRslice';
import OKRSidePanelSkeleton from './OKRSidePanelSkeleton';
import PrimaryPanel from './PrimaryPanel/PrimaryPanel';
import { checkDetailsModified } from './PrimaryPanel/Sections/ChangeDetails/ChangeDetails';
import { checkCheckInValuesModified } from './PrimaryPanel/Sections/CheckIn/CheckIn';
import { getSelectedOption } from './PrimaryPanel/Sections/Overview/OKRStatus';
import { SecondaryPanel } from './SecondaryPanel/SecondaryPanel';
import { MIN_TITLE_LENGTH_TO_ENABLE_AI_BUTTON, OKR_NOT_FOUND } from './constants';
import { useGetKRDetailsQuery, useUpdateKRMutation } from './datahooks';
import { useOKRTagsQuery } from './datahooks/useGetCustomTagsOrganization';
import { closeSecondaryPanel } from './krDrawerSecondaryPanelSlice';
import { useKRPanelParamsMatch } from './useKRPanelParamsMatch';

export const getOKRIcon = (isObjective, visibilityType) => {
  if (isObjective) {
    if (visibilityType === 'PRIVATE') return (props) => <PrivateObjective color="icon.g2" boxSize="18" {...props} />;

    return (props) => <ObjectiveIcon color="icon.g2" boxSize="18" {...props} />;
  }

  if (visibilityType === 'PRIVATE') return (props) => <KeyResultPrivate2 boxSize="18" color="icon.g2" {...props} />;

  return (props) => <KeyResultIcon boxSize="18" color="icon.g2" {...props} />;
};

const Header = ({
  onClose,
  isObjective,
  hideHeaderBody = false,
  handleHeaderTitleLocalChange,
  parentState,
  isUnsavedChangesInOKR,
  isOKRPanelDisabled,
}) => {
  const history = useHistory();
  const { location } = history;
  const { objectivesName, keyResultsName } = useOrganisationSettings();
  const isOpenAsParent = Boolean(parentState);
  const isAddChildOpenedAsPrimaryPanel =
    parentState && parentState?.type === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_SIMPLE_PANEL;

  const secondaryPanelAsParentDetails = parentState?.okrDetails;
  const invalidTitleText = `Enable Maven by providing a ${MIN_TITLE_LENGTH_TO_ENABLE_AI_BUTTON}-character title or aligning to a parent ${modifyString(
    objectivesName,
    StringModifier.TITLE_CASE
  )}
/ ${modifyString(keyResultsName, StringModifier.TITLE_CASE)}`;

  const invalidTrackingText =
    'Please set Tracking Type to "Via Mesh" and Type to "Independent" to enable AI suggestions';

  const {
    text,
    uuid,
    visibilityType,
    progressType,
    currentSectionInView,
    goalClosureStatus,
    goalProgressLevel,
    parentPost,
    avgProgressPercentage,
  } = useSelector(kRDrawerSelector);

  const panelStack = getParsedPanelStack({ location });
  const dispatch = useDispatch();
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();

  const { type: secondaryPanelType } = useSelector(kRDrawerSecondaryPanelSelector);

  const isSecondaryPanelOpen = Boolean(secondaryPanelType);

  const [localChangedNameOKR, setLocalChangedNameOKR] = useState(text);
  const [headerInputFocus, setHeaderInputFocus] = useState(false);

  useEffect(() => {
    handleHeaderTitleLocalChange(localChangedNameOKR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localChangedNameOKR]);

  const { mutate } = useUpdateKRMutation(uuid);

  const handleNameChange = useCallback(
    (updates) => {
      mutate({ updates });
    },
    [mutate]
  );

  const OKRIcon = getOKRIcon(isObjective, visibilityType);
  const ParentIcon = getOKRIcon(parentPost?.type === OKR_TYPES.OBJECTIVE, parentPost?.visibilityType);

  const handleDiscardAndBack = () => {
    const searchString = getSearchStringFromLatestPanelStack({ location });

    history.push({
      search: searchString,
    });

    if (isOpenAsParent) {
      if (parentState?.type === SECONDARY_PANEL_TYPES.OPEN_CREATE_OKR_SIMPLE_PANEL) {
        dispatch(
          openCreateOKRPanel({
            okrDetails: { ...secondaryPanelAsParentDetails },
            isSkipResetState: true,
          })
        );
      } else if (parentState?.type === SECONDARY_PANEL_TYPES.CHANGE_DETAILS_PANEL) {
        dispatch(
          openChangeDetailsPanel({
            okrDetails: { ...secondaryPanelAsParentDetails },
          })
        );
      }
    }
  };

  const handleBackButtonClick = () => {
    if (isUnsavedChangesInOKR) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          dispatch(resetKR());

          handleDiscardAndBack();
        },
      });
    } else {
      dispatch(resetKR());

      handleDiscardAndBack();
    }
  };

  const isCheckInSectionInView = currentSectionInView === 'CheckIn';

  // only show status on header if overview section is not in view and currentSectionInView is not null or undefined
  const showStatusOnHeader = (!isCheckInSectionInView && currentSectionInView) || isSecondaryPanelOpen;

  const selectedOption = getSelectedOption({ goalClosureStatus, goalProgressLevel });

  const selectedOptionColors =
    selectedOption?.progressLevel === 'CLOSED'
      ? CLOSED_STATUS_COLOR_MAP[selectedOption?.value]
      : INPROGRESS_STATUS_COLOR_MAP[selectedOption?.value];

  return (
    <DrawerHeader showCloseButton onClose={onClose} width="100%">
      <Flex alignItems="center" py={2} px={2} width="100%">
        {panelStack.length > 0 ? (
          <Button variant="tertiary" StartIcon={ArrowBack} onClick={handleBackButtonClick} />
        ) : null}
        <Box flex="auto 1 1">
          {panelStack.length > 0 ? (
            <BreadCrumbComponent
              location={location}
              panelStack={panelStack}
              currentPanelBreadcrumbText={isObjective ? objectivesName : keyResultsName}
              onBreadCrumbLinkClicked={() => {
                if (isAddChildOpenedAsPrimaryPanel) {
                  dispatch(
                    openCreateOKRPanel({
                      okrDetails: { ...secondaryPanelAsParentDetails },
                      isSkipResetState: true,
                    })
                  );
                }
              }}
            />
          ) : null}

          {isAddChildOpenedAsPrimaryPanel && (
            <SectionHeader
              headerText={`Add ${
                secondaryPanelAsParentDetails?.type === OKR_TYPES.OBJECTIVE
                  ? modifyString(objectivesName, StringModifier.TITLE_CASE)
                  : modifyString(keyResultsName, StringModifier.TITLE_CASE)
              }`}
            />
          )}

          {!isAddChildOpenedAsPrimaryPanel && (
            <Box
              display="flex"
              alignItems="center"
              sx={{
                '.header-input': {
                  flex: '1',
                },
              }}
              justifyContent="space-between"
              width="100%"
            >
              <Flex flex={1} flexDir="column">
                <Flex padding="2px 0px" alignItems="center" gap="4px" alignSelf="stretch">
                  <ParentIcon boxSize="16px" />
                  <TextLabel color={themeColors.text.g2}>{parentPost?.text || 'None'}</TextLabel>
                </Flex>
                {/*  to avoid render with undefined text , text is used as  the initial value and its value is not used if not present during first render  */}
                {!hideHeaderBody && text !== undefined && (
                  <OKRHeaderInput
                    type={panelStack.length > 0 ? 'withBreadCrumbs' : 'normal'}
                    text={text}
                    onChange={(curText) => handleNameChange({ text: curText })}
                    isObjective={isObjective}
                    onLocalOKRNameChange={(localChangedName) => setLocalChangedNameOKR(localChangedName)}
                    leftComponent={<OKRIcon />}
                    onFocusChange={setHeaderInputFocus}
                    isOKRPanelDisabled={isOKRPanelDisabled}
                  />
                )}
              </Flex>

              <Flex alignItems="center" marginLeft="12px">
                {checkGoalGenAIEnabled() && headerInputFocus ? (
                  <Box display="flex" flexDir="column">
                    <MeshIconButton
                      isDisabled={(!parentPost && localChangedNameOKR?.length < 10) || isSecondaryPanelOpen}
                      tooltipText="Rephrase with Maven"
                      onClick={() => {
                        sendAIButtonClickedEvent({
                          eventProperties: {
                            source: GOALS_EVENT_SOURCES.GOAL_TITLE,
                            type: GOALS_EVENT_SOURCES.AI.type.HELP_ME_WRITE,
                            goalId: uuid || 'NA',
                            purpose: GOALS_EVENT_SOURCES.AI.purpose.Goal_Editing,
                          },
                        });
                        dispatch(
                          openTitleAndMetricPanel({
                            isNewOKR: false,
                            isOpenedFromHeaderOKRSidePanel: true,
                          })
                        );

                        sendMeshMavenClickedEvent({
                          eventProperties: {
                            goalId: uuid,
                            page: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
                            purpose: 'Goal Title',
                            source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
                            type: 'NO',
                          },
                        });
                      }}
                      disabledReason={
                        isSecondaryPanelOpen
                          ? 'Please close right panel to enable Maven'
                          : 'AI doesn’t have enough information'
                      }
                    />
                    <TextLabel
                      fontSize="8px"
                      fontWeight="bold"
                      lineHeight="14px"
                      bgGradient="linear(90deg, #038EEF 9.05%, #ED3C65 86.39%)"
                      bgClip="text"
                    >
                      Ask Maven
                    </TextLabel>
                  </Box>
                ) : null}
                {showStatusOnHeader && !isMobileView() && (
                  <Box mx="8px" display="flex">
                    <TextBodyMedium bold color={selectedOptionColors?.textColor}>
                      {selectedOption?.label}&nbsp;
                    </TextBodyMedium>
                    <TextBodyMedium bold> • {`${Number(avgProgressPercentage)?.toFixed(2)}%`}</TextBodyMedium>
                  </Box>
                )}

                {!hideHeaderBody && !isSecondaryPanelOpen && !isOKRPanelDisabled && (
                  <OKRKebabMenu
                    isModifyTitleMenuDisabled={!parentPost && localChangedNameOKR?.length < 10}
                    disabledReason={{
                      title: invalidTitleText,
                      tracking: progressType !== OKR_PROGRESS_TYPES.MANUAL ? invalidTrackingText : invalidTitleText,
                    }}
                    isModifyTrackingMenuDisabled={
                      (!parentPost && localChangedNameOKR?.length < 10) || progressType !== OKR_PROGRESS_TYPES.MANUAL
                    }
                  />
                )}
              </Flex>
            </Box>
          )}
        </Box>
      </Flex>
    </DrawerHeader>
  );
};

const OKRSidePanel = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [localChangedNameOKR, setLocalChangedNameOKR] = useState('');
  const okrDetails = useSelector(kRDrawerSelector);

  const { localStateValues, localCheckInStateValues, uuid } = okrDetails || {};
  const { data: customTags = [] } = useOKRTagsQuery({ okrId: uuid });

  const isCheckInValuesModified = checkCheckInValuesModified(localCheckInStateValues, okrDetails);

  const isCommentModified = localCheckInStateValues?.isCommentModified || false;
  const isDetailsChanged = checkDetailsModified(localStateValues, okrDetails, customTags);

  const isUnsavedChangesInOKR = isCheckInValuesModified || isCommentModified || isDetailsChanged;

  const { isOKRPanelOpen, ids, isObjective } = useKRPanelParamsMatch();
  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();

  const queryClient = useQueryClient();

  const closeBottomDrawer = () => {
    dispatch(resetKR());
    dispatch(closeSecondaryPanel());
    queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);

    if (isObjective) {
      const route = getRouteAfterRemovingObjectiveDetailsAndKRDetails({
        location: history.location,
      });
      dispatch(closeObjectiveDetailsPanelActionCreator({ pushToHistory: true, history }));

      history.push(route);
    } else {
      const route = getRouteAfterRemovingTaskDetails({ location: history.location });
      history.push(route);
    }

    dispatch(resetKR());
  };

  const onClose = () => {
    if (isUnsavedChangesInOKR) {
      showUnsavedDataAlertDialog({
        onCancelCallback: () => {
          dispatch(resetKR());

          closeBottomDrawer();
        },
      });
    } else {
      closeBottomDrawer();
    }
  };

  const {
    data: keyResult,
    isLoading,
    error: errorFetchingOkr,
  } = useGetKRDetailsQuery({
    enabled: isOKRPanelOpen,
    id: ids?.[0],
  });

  function isAccessDeniedForOkr() {
    if (errorFetchingOkr?.message && errorFetchingOkr.message.includes('Access Denied')) {
      return true;
    }
    return false;
  }

  const isOkrNotFound =
    (errorFetchingOkr && errorFetchingOkr.status === 500 && errorFetchingOkr?.message === OKR_NOT_FOUND) ||
    isAccessDeniedForOkr();

  const { type, details } = useSelector(kRDrawerSecondaryPanelSelector);
  const isSecondaryPanelOpen = type !== null;

  const mobileViewProps = isMobileView()
    ? {
        marginLeft: '0px',
        marginRight: '0px',
        width: isSecondaryPanelOpen ? `${secondaryDrawerWidth}px` : `${primaryDrawerWidth}px`,
      }
    : {};

  useEffect(() => {
    if (!isOKRPanelOpen) {
      dispatch(resetKR());
    }
    if (isOKRPanelOpen && keyResult) {
      dispatch(updateKR(keyResult));
    }
  }, [dispatch, isOKRPanelOpen, keyResult]);

  const location = useLocation();

  // Disable OKR panel and hide tabs when user is on bulk check-in page
  const isBulkCheckInScreen = location.pathname.includes('goal/check-in/');

  return (
    <BottomDrawer isOpen={isOKRPanelOpen} placement="bottom" onClose={onClose} autoFocus={false}>
      <DrawerOverlay />
      <DrawerContent
        width={isSecondaryPanelOpen ? `${primaryDrawerWidth + secondaryDrawerWidth}px` : `${primaryDrawerWidth}px`}
        {...mobileViewProps}
        padding="4px"
        flexDirection="column"
        alignItems="flex-start"
        gap="4px"
        borderRadius="8px 8px 0px 0px"
        background="#ffffff"
      >
        {isLoading ? (
          <OKRSidePanelSkeleton isLoaded={!isLoading} />
        ) : (
          <>
            <Header
              onClose={onClose}
              isObjective={isObjective}
              hideHeaderBody={isOkrNotFound}
              handleHeaderTitleLocalChange={setLocalChangedNameOKR}
              isSecondaryPanelOpen={isSecondaryPanelOpen}
              parentState={details?.parentState}
              isUnsavedChangesInOKR={isUnsavedChangesInOKR}
              isOKRPanelDisabled={isBulkCheckInScreen}
            />

            <DrawerBody px={2} width="100%">
              {isOkrNotFound ? (
                <DataUnavailable />
              ) : (
                <Flex borderRadius="8px" h="100%">
                  {!(isMobileView() && isSecondaryPanelOpen) &&
                    (details?.parentState ? (
                      <SecondaryPanel localChangedNameOKR={localChangedNameOKR} isOpenAsParent />
                    ) : (
                      <PrimaryPanel isBulkCheckInScreen={isBulkCheckInScreen} />
                    ))}
                  {isSecondaryPanelOpen ? <SecondaryPanel localChangedNameOKR={localChangedNameOKR} /> : null}
                </Flex>
              )}
            </DrawerBody>
          </>
        )}
      </DrawerContent>
    </BottomDrawer>
  );
};

export default OKRSidePanel;
